import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  Users,
  BookOpen,
  Play,
  Pause,
  AlertCircle
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';

interface ScheduledAssignmentSystemProps {
  users: any[];
  templates: any[];
}

// Sample scheduled assignments (in real app, these would come from database)
const SAMPLE_SCHEDULED_ASSIGNMENTS = [
  {
    id: '1',
    name: 'Q1 Frontend Onboarding',
    templateId: 'frontend-fundamentals',
    templateName: 'Frontend Fundamentals',
    userIds: ['user1', 'user2'],
    scheduledDate: '2024-02-01',
    status: 'scheduled',
    createdAt: '2024-01-15',
    recurring: false
  },
  {
    id: '2',
    name: 'Monthly Backend Training',
    templateId: 'backend-essentials',
    templateName: 'Backend Essentials',
    userIds: ['user3', 'user4', 'user5'],
    scheduledDate: '2024-02-15',
    status: 'active',
    createdAt: '2024-01-10',
    recurring: true,
    recurringPattern: 'monthly'
  }
];

const ScheduledAssignmentSystem: React.FC<ScheduledAssignmentSystemProps> = ({
  users,
  templates
}) => {
  const [scheduledAssignments, setScheduledAssignments] = useState(SAMPLE_SCHEDULED_ASSIGNMENTS);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [newAssignment, setNewAssignment] = useState({
    name: '',
    templateId: '',
    userIds: [] as string[],
    scheduledDate: '',
    recurring: false,
    recurringPattern: 'monthly'
  });

  const handleCreateScheduledAssignment = () => {
    if (!newAssignment.name || !newAssignment.templateId || !newAssignment.scheduledDate || newAssignment.userIds.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    const template = templates.find(t => t.id === newAssignment.templateId);
    const newScheduled = {
      id: Date.now().toString(),
      ...newAssignment,
      templateName: template?.name || 'Unknown Template',
      status: 'scheduled',
      createdAt: new Date().toISOString().split('T')[0]
    };

    setScheduledAssignments(prev => [...prev, newScheduled]);
    setIsCreateDialogOpen(false);
    setNewAssignment({
      name: '',
      templateId: '',
      userIds: [],
      scheduledDate: '',
      recurring: false,
      recurringPattern: 'monthly'
    });
    toast.success('Scheduled assignment created successfully');
  };

  const handleEditAssignment = (assignment: any) => {
    setSelectedAssignment(assignment);
    setNewAssignment({
      name: assignment.name,
      templateId: assignment.templateId,
      userIds: assignment.userIds,
      scheduledDate: assignment.scheduledDate,
      recurring: assignment.recurring,
      recurringPattern: assignment.recurringPattern || 'monthly'
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteAssignment = (assignmentId: string) => {
    setScheduledAssignments(prev => prev.filter(a => a.id !== assignmentId));
    toast.success('Scheduled assignment deleted');
  };

  const handleToggleStatus = (assignmentId: string) => {
    setScheduledAssignments(prev => prev.map(assignment => 
      assignment.id === assignmentId 
        ? { ...assignment, status: assignment.status === 'active' ? 'paused' : 'active' }
        : assignment
    ));
    toast.success('Assignment status updated');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'active':
        return <Play className="h-4 w-4 text-green-600" />;
      case 'paused':
        return <Pause className="h-4 w-4 text-orange-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'secondary';
      case 'active':
        return 'default';
      case 'paused':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getUserNames = (userIds: string[]) => {
    return users
      .filter(user => userIds.includes(user.id))
      .map(user => user.name)
      .slice(0, 3);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Scheduled Assignment System</h3>
          <p className="text-sm text-muted-foreground">
            Schedule course assignments for future dates and create recurring assignments
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Schedule Assignment
        </Button>
      </div>

      {/* Scheduled Assignments List */}
      <div className="space-y-4">
        {scheduledAssignments.map((assignment) => (
          <Card key={assignment.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Calendar className="h-8 w-8 text-primary" />
                  <div>
                    <h4 className="font-semibold">{assignment.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      Template: {assignment.templateName}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        <span>Scheduled: {assignment.scheduledDate}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <Users className="h-3 w-3" />
                        <span>{assignment.userIds.length} user(s)</span>
                      </div>
                      {assignment.recurring && (
                        <Badge variant="outline" className="text-xs">
                          Recurring {assignment.recurringPattern}
                        </Badge>
                      )}
                    </div>
                    {assignment.userIds.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">
                          Assigned to: {getUserNames(assignment.userIds).join(', ')}
                          {assignment.userIds.length > 3 && ` +${assignment.userIds.length - 3} more`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(assignment.status)}
                      <Badge variant={getStatusColor(assignment.status) as any}>
                        {assignment.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Created: {assignment.createdAt}
                    </p>
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(assignment.id)}
                    >
                      {assignment.status === 'active' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditAssignment(assignment)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteAssignment(assignment.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {scheduledAssignments.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No scheduled assignments</h3>
              <p className="text-muted-foreground mb-4">
                Create your first scheduled assignment to automate course assignments
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Assignment
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Scheduled Assignment Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Assignment</DialogTitle>
            <DialogDescription>
              Schedule a course assignment for a future date
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="assignment-name">Assignment Name *</Label>
              <Input
                id="assignment-name"
                value={newAssignment.name}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Q1 Frontend Training"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Template *</Label>
              <Select 
                value={newAssignment.templateId} 
                onValueChange={(value) => setNewAssignment(prev => ({ ...prev, templateId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduled-date">Scheduled Date *</Label>
              <Input
                id="scheduled-date"
                type="date"
                value={newAssignment.scheduledDate}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, scheduledDate: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Users *</Label>
              <p className="text-sm text-muted-foreground">
                {newAssignment.userIds.length} user(s) selected
              </p>
              {/* In real implementation, this would be a multi-select component */}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateScheduledAssignment}>
              Schedule Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scheduled Assignment Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Scheduled Assignment</DialogTitle>
            <DialogDescription>
              Modify the scheduled assignment details
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-assignment-name">Assignment Name *</Label>
              <Input
                id="edit-assignment-name"
                value={newAssignment.name}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-scheduled-date">Scheduled Date *</Label>
              <Input
                id="edit-scheduled-date"
                type="date"
                value={newAssignment.scheduledDate}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, scheduledDate: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              // In real implementation, this would update the database
              toast.success('Scheduled assignment updated');
              setIsEditDialogOpen(false);
            }}>
              Update Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledAssignmentSystem;